import { ChevronDown, Loader } from '@firmable/icons';
import * as Select from '@radix-ui/react-select';
import React from 'react';
import { cn } from '../shadcn/cn';
import {
  SelectContent as ShadcnContent,
  SelectGroup as ShadcnGroup,
  SelectItem as ShadcnItem,
  SelectLabel as ShadcnLabel,
  Select as ShadcnRoot,
  SelectSeparator as ShadcnSeparator,
  SelectTrigger as ShadcnTrigger,
  SelectValue as ShadcnValue,
} from '../shadcn/select';

function Root({ children, ...rest }: Select.SelectProps) {
  return <ShadcnRoot {...rest}>{children}</ShadcnRoot>;
}

type SelectSize = 'lg' | 'sm';

interface TriggerProps extends Select.SelectTriggerProps {
  isLoading?: boolean;
  size?: SelectSize;
}

const triggerSizeStyles: Record<SelectSize, string> = {
  lg: 'p-sm gap-sm rounded-radius-md',
  sm: 'px-sm py-xs gap-xs rounded-radius-sm',
};

function Trigger({ className, children, isLoading, size = 'lg', ...rest }: TriggerProps) {
  const iconSize = size === 'lg' ? 16 : 14;

  return (
    <ShadcnTrigger
      className={cn(
        'h-auto w-auto whitespace-normal bg-surface-white border border-stroke-light shadow-none text-[length:inherit] text-content-dark data-[placeholder]:!text-content-light transition-colors',
        'hover:border-stroke-dark focus:border-stroke-interactive focus:ring-1 focus:ring-stroke-interactive',
        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
        triggerSizeStyles[size],
        className,
      )}
      icon={
        isLoading ? (
          <Loader.Circle size={iconSize} className="shrink-0 text-content-dark" />
        ) : (
          <ChevronDown size={iconSize} className="shrink-0 text-content-dark" />
        )
      }
      {...rest}>
      {children}
    </ShadcnTrigger>
  );
}

function Portal({ children, ...rest }: Select.SelectPortalProps) {
  return <Select.Portal {...rest}>{children}</Select.Portal>;
}

function Value({ children, ...rest }: Select.SelectValueProps) {
  return <ShadcnValue {...rest}>{children}</ShadcnValue>;
}

function Group({ children, ...rest }: Select.SelectGroupProps) {
  return <ShadcnGroup {...rest}>{children}</ShadcnGroup>;
}

function Label({ children, className, ...rest }: Select.SelectLabelProps) {
  return (
    <ShadcnLabel className={cn('p-0 text-[length:inherit] font-normal', className)} {...rest}>
      {children}
    </ShadcnLabel>
  );
}

function Separator({ children, className, ...rest }: Select.SelectSeparatorProps) {
  return (
    <ShadcnSeparator className={cn('m-0 h-auto bg-transparent', className)} {...rest}>
      {children}
    </ShadcnSeparator>
  );
}

export interface ContentProps extends Select.SelectContentProps {
  contentClassName?: string;
}

// The skin sits on the viewport, as before; the content box around it stays unpainted.
const Content = React.forwardRef<HTMLDivElement, ContentProps>(
  ({ children, className, contentClassName, ...rest }, ref) => {
    return (
      <ShadcnContent
        position="popper"
        sideOffset={3}
        scrollButtons={false}
        className={cn(
          'min-w-0 overflow-visible rounded-none border-0 bg-transparent shadow-none text-[length:inherit] text-content-dark data-[side=bottom]:translate-y-0 data-[side=top]:translate-y-0 data-[side=left]:translate-x-0 data-[side=right]:translate-x-0',
          contentClassName,
        )}
        viewportClassName={cn(
          'h-auto min-w-[var(--radix-popper-anchor-width)] rounded-radius-sm shadow-overlay bg-surface-white p-sm flex flex-col gap-xs',
          className,
        )}
        {...rest}
        ref={ref}>
        {children}
      </ShadcnContent>
    );
  },
);

Content.displayName = 'SelectContent';

export interface ItemProps extends Select.SelectItemProps {
  isSelected: boolean;
}

// Radix focuses the selected item when the list opens, and Chrome counts that as keyboard focus even after a
// mouse click, so a ring would show on every open. Focus shows as the hover tint instead: an unselected item
// focused by the arrow keys looks hovered; the selected item keeps its tint on open and darkens only on hover.
const Item = React.forwardRef<HTMLDivElement, ItemProps>(({ children, className, isSelected, ...rest }, ref) => {
  return (
    <ShadcnItem
      ref={ref}
      indicator={false}
      className={cn(
        'block cursor-pointer rounded-radius-md p-sm text-body-large focus-visible:ring-0',
        'data-[disabled]:pointer-events-auto data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
        {
          'bg-surface-white text-content-dark hover:bg-surface-light focus:bg-surface-light focus:text-content-dark':
            !isSelected,
          'bg-surface-interactive-lighter text-content-interactive hover:bg-surface-interactive-light focus:bg-surface-interactive-lighter focus:text-content-interactive focus:hover:bg-surface-interactive-light':
            isSelected,
        },
        className,
      )}
      {...rest}>
      {children}
    </ShadcnItem>
  );
});

Item.displayName = 'SelectItem';

function ItemText({ children, className }: Select.SelectItemTextProps) {
  return <Select.ItemText className={cn(className)}>{children}</Select.ItemText>;
}
function ItemIndicator({ children, className }: Select.SelectItemIndicatorProps) {
  return <Select.ItemIndicator className={cn(className)}>{children}</Select.ItemIndicator>;
}

export { Content, Group, Item, ItemIndicator, ItemText, Label, Portal, Root, Separator, Trigger, Value };
